import { Card } from "../Card.js";
import { Empty, Failed, LoadOlder, Loading } from "../ViewState.js";
import { formatKwh } from "../../lib/energy.js";
import { formatPercent } from "../../lib/format.js";
import {
  chargeNote,
  counterNote,
  coverageNote,
  edgeNote,
  formatSpan,
  outageWhen,
} from "../../lib/outages.js";
import { useOutages, type OutageRow } from "../../lib/usePages.js";
import { cn } from "../../lib/cn.js";

/**
 * Every grid outage, newest first: how long it lasted, what the battery and
 * the panels gave, and how much of it the house kept power through
 * (`REQUIREMENTS.md` section 1, job 3; docs/DECISIONS/0034).
 *
 * Coverage is time, not energy. Where some of an outage went unrecorded, the
 * share is a floor and the row says so; where the record stopped before the
 * grid came back, the row says the battery most likely ran out. Neither is
 * left for a reader to infer from a percentage.
 *
 * Refreshed with each new reading, so an outage still going keeps counting
 * while the page is open.
 */
export function OutagesCard({ refreshKey }: { refreshKey: string | null }) {
  const { state, loadingMore, moreFailed, reload, loadMore } = useOutages(refreshKey);

  return (
    <Card title="Grid outages">
      {state.kind === "loading" ? (
        <Loading label="Loading the outage history…" />
      ) : state.kind === "error" ? (
        <Failed message={state.message} onRetry={reload} />
      ) : state.items.length === 0 ? (
        <Empty>
          No grid outage has been recorded. When the grid drops out, it appears here with how long
          it lasted and what the battery gave.
        </Empty>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="max-w-prose text-xs leading-relaxed text-ink-muted">
            Times are the site&rsquo;s. Readings arrive every few minutes, so each edge is known to
            within one of them.
          </p>
          <OutageCards outages={state.items} />
          <OutageTable outages={state.items} />
          {state.nextBefore !== null && (
            <LoadOlder
              loading={loadingMore}
              failed={moreFailed}
              failedMessage="Could not load older outages. Try again."
              onLoad={loadMore}
            />
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * How much of the outage the house kept power through: the figure, and a bar
 * beside it that repeats it. The words carry it; the bar is for scanning.
 */
function Coverage({ outage }: { outage: OutageRow }) {
  const share = Math.max(0, Math.min(1, outage.coveredShare));
  return (
    <span className="flex items-center gap-2">
      <span className="tabular text-ink">{formatPercent(share)}</span>
      <span aria-hidden="true" className="h-1.5 w-16 overflow-hidden rounded-full bg-line">
        <span
          className={cn("block h-full rounded-full", share < 1 ? "bg-danger" : "bg-accent")}
          style={{ width: `${String(share * 100)}%` }}
        />
      </span>
    </span>
  );
}

/** The outage's span, then everything a reader needs to weigh its figures. */
function When({ outage }: { outage: OutageRow }) {
  const coverage = coverageNote(outage);
  return (
    <span className="flex flex-col gap-1">
      <span className="font-medium text-ink">
        {outageWhen(outage)}
        {outage.ongoing && (
          <span className="ml-2 rounded-full border border-danger/40 px-2 py-0.5 text-[11px] font-semibold text-danger-ink">
            Ongoing
          </span>
        )}
      </span>
      <span className="text-xs font-normal text-ink-muted">{edgeNote(outage)}</span>
      {coverage !== null && <span className="text-xs font-normal text-danger-ink">{coverage}</span>}
    </span>
  );
}

function Charge({ outage }: { outage: OutageRow }) {
  return (
    <span className="flex flex-col">
      <span className="tabular text-ink">{`${String(outage.socStartPct)}% → ${String(outage.socEndPct)}%`}</span>
      <span className={cn("text-xs", outage.reachedReserve ? "text-danger-ink" : "text-ink-muted")}>
        {chargeNote(outage)}
      </span>
    </span>
  );
}

const FIGURES = [
  { label: "Lasted", short: true, value: (o: OutageRow) => formatSpan(o.durationSeconds) },
  { label: "Battery covered", short: true, value: (o: OutageRow) => <Coverage outage={o} /> },
  { label: "From the battery", short: true, value: (o: OutageRow) => formatKwh(o.batteryKwh) },
  { label: "From the panels", short: true, value: (o: OutageRow) => formatKwh(o.solarKwh) },
  { label: "Charge", short: false, value: (o: OutageRow) => <Charge outage={o} /> },
  { label: "Inverter's counter", short: false, value: (o: OutageRow) => counterNote(o) },
] as const;

/**
 * Below the desktop breakpoint: one stacked card per outage. Section 4 asks
 * for cards below the tablet one; seven columns with notes need more room
 * than a tablet has, and at 768px the durations broke across lines.
 */
function OutageCards({ outages }: { outages: readonly OutageRow[] }) {
  return (
    <ul className="flex flex-col divide-y divide-line lg:hidden">
      {outages.map((outage) => (
        <li key={outage.start} className="py-4">
          <When outage={outage} />
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {FIGURES.map((figure) => (
              <div key={figure.label} className="contents">
                <dt className="text-ink-muted">{figure.label}</dt>
                <dd className="flex justify-end text-right text-ink">{figure.value(outage)}</dd>
              </div>
            ))}
          </dl>
        </li>
      ))}
    </ul>
  );
}

/** From the desktop breakpoint up: one row per outage, short figures kept whole. */
function OutageTable({ outages }: { outages: readonly OutageRow[] }) {
  return (
    <table className="hidden w-full text-left text-sm lg:table">
      <thead>
        <tr className="border-b border-line text-xs font-medium text-ink-muted">
          <th scope="col" className="pb-4 pr-4 font-normal">
            When
          </th>
          {FIGURES.map((figure) => (
            <th key={figure.label} scope="col" className="pb-4 pr-4 font-normal">
              {figure.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-line">
        {outages.map((outage) => (
          <tr key={outage.start} className="align-top transition-colors hover:bg-line/40">
            <th scope="row" className="max-w-xs py-4 pr-4 text-left font-medium">
              <When outage={outage} />
            </th>
            {FIGURES.map((figure) => (
              <td
                key={figure.label}
                className={cn("py-4 pr-4 text-ink-muted", figure.short && "whitespace-nowrap")}
              >
                {figure.value(outage)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
